import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Menu, X, LayoutDashboard, Settings, LogOut, MessageSquarePlus, Moon, Sparkles, BookOpen, Trash2, Zap, Target, Calendar, ChevronLeft, ChevronRight, Pencil, Check, Lock, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import ChatInput from '@/components/ChatInput';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png"; // Small "a.gpt" logo for internal pages
const DEA_AVATAR = "https://customer-assets.emergentagent.com/job_custom-gpt-clone/artifacts/zesh7gd7_Deon_Stokes_photo_9.jpg";

export default function Chat() {
  const { user, getAuthHeader, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [cyclePhase, setCyclePhase] = useState(null);
  const [dailyInspiration, setDailyInspiration] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const messagesEndRef = useRef(null);
  const messagesStartRef = useRef(null);
  const isInitialLoad = useRef(true);
  const lastMessageRef = useRef(null);
  const previousMessageCount = useRef(0);
  
  // Trial access state
  const [accessLevel, setAccessLevel] = useState(null);

  // Helper function to strip markdown from text for display
  const stripMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove bold **text**
      .replace(/\*(.*?)\*/g, '$1')       // Remove italic *text*
      .replace(/#{1,6}\s/g, '')          // Remove headers
      .replace(/[-•]\s/g, '')            // Remove list markers
      .replace(/\n+/g, ' ')              // Replace newlines with space
      .trim();
  };

  // Rename a chat session
  const renameSession = async (sid, newTitle) => {
    if (!newTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    try {
      await axios.put(
        `${API}/chat/sessions/${sid}/rename`,
        { title: newTitle.trim() },
        { headers: getAuthHeader() }
      );
      setSessions(prev => prev.map(s => 
        s.session_id === sid ? { ...s, title: newTitle.trim() } : s
      ));
      setEditingSessionId(null);
      toast.success('Chat renamed');
    } catch (error) {
      toast.error('Failed to rename chat');
    }
  };

  // Fetch data on mount - with memory persistence
  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        // Get user's timezone from browser
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        const [sessionsRes, cycleRes, inspirationRes, accessRes] = await Promise.all([
          axios.get(`${API}/chat/sessions`, { headers: getAuthHeader() }),
          axios.get(`${API}/user/cycle-phase?tz=${encodeURIComponent(userTimezone)}`, { headers: getAuthHeader() }),
          axios.get(`${API}/user/daily-inspiration`, { headers: getAuthHeader() }),
          axios.get(`${API}/user/access-level`, { headers: getAuthHeader() })
        ]);
        
        const fetchedSessions = sessionsRes.data.sessions || [];
        setSessions(fetchedSessions);
        
        if (cycleRes.data.enabled) {
          setCyclePhase(cycleRes.data.phase);
        }
        setDailyInspiration(inspirationRes.data);
        setAccessLevel(accessRes.data);
        console.log('🔐 Chat Access Level:', accessRes.data);
        
        // MEMORY PERSISTENCE: Check if user has previous conversations
        // If they have sessions, load the most recent one to maintain context
        if (fetchedSessions.length > 0) {
          // User has previous conversations - load the most recent session
          const mostRecentSession = fetchedSessions[0]; // Already sorted by most recent
          try {
            const historyRes = await axios.get(
              `${API}/chat/history/${mostRecentSession.session_id}`, 
              { headers: getAuthHeader() }
            );
            const previousMessages = historyRes.data.messages || [];
            
            if (previousMessages.length > 0) {
              // Load previous conversation
              setSessionId(mostRecentSession.session_id);
              setMessages(previousMessages);
              setInitialLoading(false);
              return; // Exit early - we've loaded the conversation
            }
          } catch (historyError) {
            console.log('Could not load previous session, starting fresh');
          }
        }
        
        // NEW USER or no previous messages: Show welcome greeting
        // Check if user has completed onboarding (has a journey)
        const inspiration = inspirationRes.data;
        let greeting = `Hey ${user?.name || 'love'}! I'm **Dea** — trained by Deon to help you build the business and life you're dreaming of.\n\n`;
        
        if (inspiration?.scripture) {
          greeting += `✨ **Today's Scripture:**\n*"${inspiration.scripture.text}"*\n— ${inspiration.scripture.verse}\n\n`;
        }
        
        if (inspiration?.affirmation) {
          greeting += `💫 **Today's Affirmation:**\n*"${inspiration.affirmation}"*\n\n`;
        }
        
        // Check if user has completed onboarding via their journey
        if (user?.onboarding_complete || user?.current_day > 0) {
          // Returning user who completed onboarding - welcome back message
          greeting += `Welcome back, sis! Ready to keep crushing those goals?\n\n`;
          greeting += `**What's on your mind today?** I'm here to help you stay on track with your 90-day journey.`;
        } else {
          // New user - start onboarding questions
          greeting += `Before we jump into the *how*, I want to really understand your vision. We're going one question at a time, so this doesn't feel overwhelming.\n\n`;
          greeting += `**What is the ONE outcome you most want to achieve in your life or business right now?**\n\n`;
          greeting += `(This can be a revenue goal, lifestyle shift, identity change, or season-defining win. No number is too big. No dream is too much.)`;
        }
        
        setMessages([{
          role: 'assistant',
          content: greeting,
          timestamp: new Date().toISOString()
        }]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Set default greeting without inspiration
        setMessages([{
          role: 'assistant',
          content: `Hey ${user?.name || 'love'}! I'm **Dea** — trained by Deon to help you build the business and life you're dreaming of.\n\n**What's on your mind today?**`,
          timestamp: new Date().toISOString()
        }]);
      } finally {
        setInitialLoading(false);
      }
    };
    if (user) {
      fetchData();
    }
  }, [user, getAuthHeader]);

  // Get user timezone for API calls
  const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Remove the separate greeting useEffect since it's now in fetchData

  // Auto scroll - show bottom on load, but show START of new messages
  useEffect(() => {
    if (messages.length > 0) {
      // Check if new message was added
      const isNewMessage = messages.length > previousMessageCount.current;
      previousMessageCount.current = messages.length;
      
      if (isInitialLoad.current) {
        // On initial load, scroll to bottom to show most recent
        // Use setTimeout to ensure DOM is rendered
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 100);
        isInitialLoad.current = false;
      } else if (isNewMessage && lastMessageRef.current) {
        // When new message arrives, scroll to show START of that message
        // Use scrollIntoView with 'start' alignment to show top of message
        setTimeout(() => {
          lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [messages]);

  const handleSend = async (overrideMessage) => {
    // overrideMessage is now ALWAYS the message text — ChatInput passes its own
    // local state via onSend(text), and quick-prompt buttons pass their preset
    // string. No more reading from a parent input state (eliminates re-render lag).
    const messageText = typeof overrideMessage === 'string' ? overrideMessage.trim() : '';

    // Don't send if empty or already loading
    if (!messageText || loading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    // Retry logic - try up to 3 times
    const maxRetries = 3;
    let lastError = null;
    
    // Debug logging for production issues
    console.log('=== CHAT DEBUG ===');
    console.log('API URL:', API);
    console.log('Full chat URL:', `${API}/chat`);
    console.log('Auth header:', getAuthHeader());
    console.log('Session ID:', sessionId);
    console.log('Message:', userMessage.content.substring(0, 50));
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Chat attempt ${attempt}/${maxRetries}...`);
        const response = await axios.post(
          `${API}/chat`,
          { 
            message: userMessage.content, 
            session_id: sessionId,
            timezone: getUserTimezone()
          },
          { 
            headers: getAuthHeader(),
            timeout: 60000 // 60 second timeout
          }
        );
        console.log('Chat response received:', response.status);

        setSessionId(response.data.session_id);
        
        // Check if journey was reset - notify user
        if (response.data.journey_reset) {
          toast.success('Your 90-Day Journey has been reset! Day 1 starts now.', { duration: 6000 });
        }
        
        // Refresh the sessions list to include this conversation
        try {
          const sessionsRes = await axios.get(`${API}/chat/sessions`, { headers: getAuthHeader() });
          setSessions(sessionsRes.data.sessions || []);
        } catch (e) {
          console.log('Could not refresh sessions list');
        }
        
        // Check if thread limit is reached
        let assistantContent = response.data.response;
        if (response.data.thread_limit_reached) {
          assistantContent += "\n\n---\n\n✨ **Hey girl, we've reached the end of this chat thread, but don't worry — I'll pick up our conversation in a new chat!** Click the ✚ button to start a fresh thread and let's keep building! 💪";
        }
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: assistantContent,
          timestamp: new Date().toISOString()
        }]);
        setLoading(false);
        return; // Success - exit the function
      } catch (error) {
        lastError = error;
        console.error(`Chat error (attempt ${attempt}/${maxRetries}):`, error);
        console.error('Error response:', error.response?.data);
        
        // If not the last attempt, wait before retrying
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    // All retries failed - show more specific error message
    let errorMessage = 'Dea is taking a moment. Please try again.';
    if (lastError?.response?.status === 504) {
      errorMessage = 'Dea is thinking too hard! Try a simpler question.';
    } else if (lastError?.response?.data?.detail) {
      // Show the actual error from the server for debugging
      errorMessage = `Error: ${lastError.response.data.detail.substring(0, 100)}`;
      console.error('Server error detail:', lastError.response.data.detail);
    }
    toast.error(errorMessage, { duration: 8000 });
    setMessages(prev => prev.slice(0, -1));
    setLoading(false);
  };

  // handleKeyDown REMOVED (Feb 2026): Enter/Return now always inserts a newline.
  // Send only happens via the gold arrow button in the ChatInput component.

  const startNewChat = () => {
    setSessionId(null);
    
    let greeting = `Hey ${user?.name || 'love'}! Starting fresh — I love it!\n\n`;
    
    // Add daily inspiration for new chat
    if (dailyInspiration?.scripture) {
      greeting += `✨ **Today's Scripture:**\n*"${dailyInspiration.scripture.text}"*\n— ${dailyInspiration.scripture.verse}\n\n`;
    }
    
    if (dailyInspiration?.affirmation) {
      greeting += `💫 **Today's Affirmation:**\n*"${dailyInspiration.affirmation}"*\n\n`;
    }
    
    greeting += `**What's on your mind today, sis?**`;
    
    setMessages([{
      role: 'assistant',
      content: greeting,
      timestamp: new Date().toISOString()
    }]);
    setMobileSidebarOpen(false);
  };

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/history/${sid}`, { headers: getAuthHeader() });
      setSessionId(sid);
      setMessages(response.data.messages || []);
      setMobileSidebarOpen(false);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation(); // Prevent triggering loadSession
    e.preventDefault();
    
    const confirmed = window.confirm("Delete this conversation? This cannot be undone.");
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/chat/session/${sid}`, { headers: getAuthHeader() });
      
      // Remove from sessions list
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      
      // If we deleted the current session, start a new chat
      if (sessionId === sid) {
        startNewChat();
      }
      
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const deleteAllChats = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete ALL your conversations? This cannot be undone. Your journey progress and coaching data will NOT be affected."
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/chat/all`, { headers: getAuthHeader() });
      setSessions([]);
      startNewChat();
      toast.success('All conversations deleted');
    } catch (error) {
      console.error('Delete all error:', error);
      toast.error('Failed to delete conversations');
    }
  };

  const formatMessage = (content) => {
    // CRITICAL: Force content to be a string - handle ANY type
    if (!content) {
      return '';
    }
    
    if (typeof content !== 'string') {
      // If it's an object, try to extract text
      if (typeof content === 'object') {
        if (content.response) return formatMessage(content.response);
        if (content.content) return formatMessage(content.content);
        if (content.text) return formatMessage(content.text);
        if (content.message) return formatMessage(content.message);
        // Last resort: stringify it
        return String(content);
      }
      // If it's any other type, convert to string
      return String(content);
    }
    
    const lines = content.split('\n');
    const result = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Handle headers
      if (line.startsWith('### ')) {
        if (inList) { result.push('</ul>'); inList = false; }
        const headerText = line.slice(4).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<h4 class="text-base font-serif font-semibold text-[#726158] mt-3 mb-1">${headerText}</h4>`);
        continue;
      }
      if (line.startsWith('## ')) {
        if (inList) { result.push('</ul>'); inList = false; }
        const headerText = line.slice(3).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<h3 class="text-lg font-serif font-bold text-[#726158] mt-3 mb-1">${headerText}</h3>`);
        continue;
      }
      if (line.startsWith('# ')) {
        if (inList) { result.push('</ul>'); inList = false; }
        const headerText = line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<h2 class="text-xl font-serif font-bold text-[#726158] mt-3 mb-1">${headerText}</h2>`);
        continue;
      }
      
      // Handle horizontal rules
      if (line.trim() === '---' || line.trim() === '***') {
        if (inList) { result.push('</ul>'); inList = false; }
        result.push(`<hr class="my-2 border-[#EAD8C2]" />`);
        continue;
      }
      
      // Handle list items - group them together
      if (line.startsWith('- ') || line.startsWith('• ')) {
        if (!inList) {
          result.push('<ul class="list-disc ml-5 my-1 space-y-0.5">');
          inList = true;
        }
        let itemText = line.slice(2);
        itemText = itemText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#726158] font-semibold">$1</strong>');
        itemText = itemText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        result.push(`<li>${itemText}</li>`);
        continue;
      }
      
      // Close list if we're no longer in one
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      
      // Handle empty lines
      if (line.trim() === '') {
        result.push('<div class="h-1"></div>');
        continue;
      }
      
      // Regular text - handle bold and italic
      let processedLine = line;
      processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#726158] font-semibold">$1</strong>');
      processedLine = processedLine.replace(/\*(.*?)\*/g, '<em>$1</em>');
      result.push(`<p class="my-0.5">${processedLine}</p>`);
    }
    
    // Close any open list
    if (inList) {
      result.push('</ul>');
    }
    
    return result.join('');
  };

  const getPhaseColor = (phase) => {
    const colors = {
      menstrual: 'phase-bg-menstrual phase-menstrual',
      follicular: 'phase-bg-follicular phase-follicular',
      ovulatory: 'phase-bg-ovulatory phase-ovulatory',
      luteal: 'phase-bg-luteal phase-luteal'
    };
    return colors[phase] || 'bg-[#F5F3EF] text-[#4A4A4A]';
  };

  // Trial access helpers
  const isTrialUser = accessLevel?.type === 'trial';
  const isExpiredTrial = accessLevel?.type === 'expired_trial';
  const isFullAccess = accessLevel?.type === 'full' || (!accessLevel?.type && user?.paid);
  const trialDaysRemaining = accessLevel?.daysRemaining || 0;
  const upgradePrice = accessLevel?.upgradeAmount || 88;

  // Trial Days Remaining Indicator (shown in header for active trial users)
  const TrialIndicator = () => {
    if (!isTrialUser) return null;
    
    return (
      <div 
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CAAB84]/20 border border-[#CAAB84]/40"
        data-testid="chat-trial-indicator"
      >
        <Clock className="h-3 w-3 text-[#CAAB84]" />
        <span className="text-xs font-medium text-[#726158]">
          {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} left
        </span>
        <Link to="/checkout?upgrade=true">
          <span className="text-xs text-[#CAAB84] hover:text-[#AA9283] underline">Upgrade</span>
        </Link>
      </div>
    );
  };

  // Expired Trial Lockout Overlay
  const ExpiredTrialOverlay = () => {
    if (!isExpiredTrial) return null;
    
    return (
      <div className="absolute inset-0 z-40 bg-[#FFFCF7]/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="chat-expired-overlay">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-[#B1897C]/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="h-10 w-10 text-[#B1897C]" />
          </div>
          <h2 className="font-serif text-2xl text-[#726158] mb-3">Your Trial Has Ended</h2>
          <p className="text-[#AA9283] mb-6">
            Your 7-day Power Start has come to an end. Upgrade now to continue chatting with Dea and unlock your full 90-day transformation journey.
          </p>
          
          {/* Show previous chat history note */}
          <div className="bg-[#EAD8C2]/30 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-[#726158] mb-2 font-medium">Your progress is safe!</p>
            <p className="text-xs text-[#AA9283]">
              All your previous conversations with Dea are saved. Upgrade to pick up right where you left off.
            </p>
          </div>
          
          <Link to="/checkout?upgrade=true">
            <Button className="btn-gold py-4 px-8 text-lg w-full" data-testid="chat-expired-upgrade-btn">
              Continue My Journey — ${upgradePrice}
            </Button>
          </Link>
          
          <Link to="/dashboard" className="block mt-4">
            <Button variant="ghost" className="text-[#AA9283] hover:text-[#726158]">
              View Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  // Memoize the rendered message list — without this, every keystroke in the input
  // triggers a re-render of every bubble (running formatMessage + dangerouslySetInnerHTML
  // for each message), causing visible typing lag in long conversations.
  const renderedMessages = useMemo(
    () => messages.map((message, index) => {
      const isLast = index === messages.length - 1;
      return (
        <motion.div
          key={index}
          ref={isLast ? lastMessageRef : null}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          {message.role === 'assistant' && (
            <img src={DEA_AVATAR} alt="Dea" className="avatar-dea mr-2 sm:mr-3 flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10" />
          )}
          <div
            className={`max-w-[88%] sm:max-w-[85%] md:max-w-[80%] p-3 sm:p-4 ${
              message.role === 'user'
                ? 'chat-bubble-user'
                : 'chat-bubble-dea'
            }`}
          >
            <div
              className={`${message.role === 'user' ? 'text-[#FFFCF7]' : 'text-[#726158]'} text-sm sm:text-base break-words overflow-wrap-anywhere`}
              dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
            />
          </div>
          {message.role === 'user' && (
            user?.profile_image ? (
              <img
                src={user.profile_image}
                alt="You"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover ml-2 sm:ml-3 flex-shrink-0 shadow-md border-2 border-[#EAD8C2]"
              />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#CAAB84] flex items-center justify-center ml-2 sm:ml-3 flex-shrink-0 shadow-md">
                <span className="text-white font-semibold text-xs sm:text-sm">
                  {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'ME'}
                </span>
              </div>
            )
          )}
        </motion.div>
      );
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, user?.profile_image, user?.name]
  );

  return (
    <>
      <SEO 
        title="Chat with Dea"
        description="Have a conversation with Dea, your AI business coach. Get personalized guidance on your 90-day journey."
        noindex={true}
      />
      <div className="h-screen bg-[#FFFCF7] flex overflow-hidden">
      {/* Loading state */}
      {initialLoading && (
        <div className="fixed inset-0 z-50 bg-[#FFFCF7] flex items-center justify-center">
          <div className="text-center">
            <div className="spinner w-10 h-10 mx-auto mb-4" />
            <p className="text-[#AA9283]">Loading your conversation...</p>
          </div>
        </div>
      )}
      
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#726158]/20 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Always visible on desktop, slide-in on mobile */}
      <aside
        className={`
          fixed lg:relative left-0 top-0 bottom-0 z-50 lg:z-auto
          bg-[#FFFCF7] border-r border-[#EAD8C2] flex flex-col
          transition-all duration-300 ease-in-out w-72
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header with logo - Fixed at top */}
        <div className="p-4 border-b border-[#EAD8C2] flex items-center justify-between flex-shrink-0">
          <Link to="/">
            <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
          </Link>
          
          {/* Mobile close button - only visible on mobile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable Content Area - ONE scroll for everything */}
        <div className="flex-1 overflow-y-auto">
          {/* New Chat Button */}
          <div className="p-4">
            <Button
              onClick={startNewChat}
              className="w-full btn-outline-gold flex items-center gap-2"
              data-testid="new-chat-btn"
              title="New Chat"
            >
              <MessageSquarePlus className="h-4 w-4 flex-shrink-0" />
              <span>New Chat</span>
            </Button>
          </div>

          {/* Daily Inspiration Card */}
          {(dailyInspiration?.scripture || dailyInspiration?.affirmation) && (
            <div className="px-4 pb-3">
              <div className="rounded-xl p-3 bg-[#87A6A7]/10 border border-[#87A6A7]/20">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-[#87A6A7] flex-shrink-0" />
                  <span className="text-sm font-medium text-[#726158]">Daily Inspiration</span>
                </div>
                {dailyInspiration?.scripture && (
                  <div className="mb-2">
                    <p className="text-xs text-[#726158] italic leading-snug">"{dailyInspiration.scripture.text}"</p>
                    <p className="text-xs text-[#87A6A7] mt-1 font-medium">— {dailyInspiration.scripture.verse}</p>
                  </div>
                )}
                {dailyInspiration?.affirmation && (
                  <p className="text-xs text-[#AA9283] italic leading-snug">"{dailyInspiration.affirmation}"</p>
                )}
              </div>
            </div>
          )}

          {/* Cycle Phase */}
          {cyclePhase && (
            <div className="px-4 pb-3">
              <div className={`rounded-xl p-3 ${getPhaseColor(cyclePhase.phase)}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Moon className="h-4 w-4" />
                  <span className="text-sm font-medium">{cyclePhase.name} Phase</span>
                </div>
                <p className="text-xs opacity-80">Day {cyclePhase.day} • {cyclePhase.focus}</p>
              </div>
            </div>
          )}

          {/* Recent Conversations */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#B1897C]">Recent Conversations</p>
              <button
                onClick={deleteAllChats}
                className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded"
                data-testid="delete-all-chats-btn"
              >
                <Trash2 className="h-3 w-3" />
                Clear All
              </button>
            </div>
            <div className="space-y-2">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    sessionId === session.session_id
                      ? 'bg-[#87A6A7]/10 text-[#726158] border border-[#87A6A7]/20'
                      : 'text-[#AA9283] hover:bg-[#EAD8C2]/50'
                  }`}
                >
                {editingSessionId === session.session_id ? (
                  /* Editing mode */
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameSession(session.session_id, editingTitle);
                        if (e.key === 'Escape') setEditingSessionId(null);
                      }}
                      className="h-8 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={() => renameSession(session.session_id, editingTitle)}
                      className="p-1.5 rounded-md text-[#87A6A7] hover:bg-[#87A6A7]/20"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  /* Normal display mode */
                  <>
                    <button
                      onClick={() => loadSession(session.session_id)}
                      className="flex-1 text-left overflow-hidden mr-2"
                    >
                      <p className="text-sm truncate">{stripMarkdown(session.title || session.last_message)}</p>
                      <p className="text-xs text-[#B1897C] mt-1">{session.count} messages</p>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {
                          setEditingSessionId(session.session_id);
                          setEditingTitle(session.title || stripMarkdown(session.last_message).slice(0, 50));
                        }}
                        className="p-1.5 rounded-md text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50"
                        title="Rename chat"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => deleteSession(session.session_id, e)}
                        className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-chat-${session.session_id}`}
                        title="Delete conversation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {sessions.length === 0 && (
              <p className="text-xs text-[#B1897C] text-center py-4">No conversations yet</p>
            )}
            </div>
          </div>
        </div>

        {/* Bottom Navigation - Fixed at bottom with tighter spacing */}
        <div className="p-3 border-t border-[#EAD8C2] space-y-1 flex-shrink-0">
          <Link to="/dashboard">
            <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50 h-9" title="Dashboard">
              <LayoutDashboard className="h-4 w-4 flex-shrink-0" />
              <span className="ml-2">Dashboard</span>
            </Button>
          </Link>
          <Link to="/reflections">
            <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50 h-9" title="Reflections">
              <BookOpen className="h-4 w-4 flex-shrink-0" />
              <span className="ml-2">Reflections</span>
            </Button>
          </Link>
          <Link to="/settings">
            <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50 h-9" title="Settings">
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span className="ml-2">Settings</span>
            </Button>
          </Link>
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-[#AA9283] hover:text-[#B1897C] hover:bg-[#B1897C]/10 h-9"
            title="Logout"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            <span className="ml-2">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Expired Trial Overlay - blocks chat for expired trial users */}
        <ExpiredTrialOverlay />
        
        {/* Header */}
        <header className="h-16 bg-[#FFFCF7]/90 backdrop-blur-lg border-b border-[#EAD8C2] flex items-center px-4 gap-4">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="lg:hidden text-[#AA9283] hover:text-[#726158]"
            data-testid="sidebar-toggle-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <img src={DEA_AVATAR} alt="Dea" className="avatar-dea" />
            <div>
              <h1 className="font-serif text-lg text-[#726158]">Dea</h1>
              <p className="text-xs text-[#B1897C]">Your AI Business Coach</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Trial Indicator - shows days remaining for active trial */}
            <TrialIndicator />
            
            {dailyInspiration?.scripture && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CAAB84]/10 border border-[#CAAB84]/20">
                <BookOpen className="h-3 w-3 text-[#CAAB84]" />
                <span className="text-xs text-[#AA9283]">{dailyInspiration.scripture.verse}</span>
              </div>
            )}
            {cyclePhase && (
              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full ${getPhaseColor(cyclePhase.phase)}`}>
                <Sparkles className="h-3 w-3" />
                <span className="text-xs font-medium">{cyclePhase.name}</span>
              </div>
            )}
          </div>
        </header>

        {/* Messages */}
        <ScrollArea className="flex-1 p-3 sm:p-4 md:p-6 bg-[#FFFCF7] overflow-x-hidden" data-testid="chat-messages">
          <div className="max-w-4xl mx-auto space-y-6 px-1 sm:px-2">
            <div ref={messagesStartRef} />
            <AnimatePresence initial={false}>
              {renderedMessages}
            </AnimatePresence>

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-start"
              >
                <img src={DEA_AVATAR} alt="Dea" className="avatar-dea mr-3 flex-shrink-0" />
                <div className="chat-bubble-dea">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input - disabled for expired trial users */}
        <div className={`p-3 md:p-4 lg:p-6 bg-[#FFFCF7] border-t border-[#EAD8C2] ${isExpiredTrial ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="max-w-4xl mx-auto px-2 sm:px-4">
            {/* Quick Action Prompts - horizontal scroll on mobile */}
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide -mx-2 px-2">
              <button
                onClick={() => handleSend("What am I supposed to do today?")}
                disabled={loading || isExpiredTrial}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#CAAB84]/10 hover:bg-[#CAAB84]/20 border border-[#CAAB84]/30 text-xs md:text-sm text-[#726158] transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                data-testid="quick-prompt-today"
              >
                <Zap className="h-3 w-3 md:h-4 md:w-4 text-[#CAAB84]" />
                Today's tasks
              </button>
              <button
                onClick={() => handleSend("How am I progressing on my 90-day goal?")}
                disabled={loading || isExpiredTrial}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#87A6A7]/10 hover:bg-[#87A6A7]/20 border border-[#87A6A7]/30 text-xs md:text-sm text-[#726158] transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                data-testid="quick-prompt-progress"
              >
                <Target className="h-3 w-3 md:h-4 md:w-4 text-[#87A6A7]" />
                My progress
              </button>
              <button
                onClick={() => handleSend("What's on my roadmap this week?")}
                disabled={loading || isExpiredTrial}
                className="flex gap-1.5 px-3 py-1.5 rounded-full bg-[#AA9283]/10 hover:bg-[#AA9283]/20 border border-[#AA9283]/30 text-xs md:text-sm text-[#726158] transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                data-testid="quick-prompt-week"
              >
                <Calendar className="h-3 w-3 md:h-4 md:w-4 text-[#AA9283]" />
                This week
              </button>
            </div>
            
            <ChatInput
              onSend={(text) => handleSend(text)}
              disabled={isExpiredTrial || loading}
              placeholder={isExpiredTrial ? "Upgrade to continue chatting..." : "Talk to Dea..."}
            />
            <p className="text-center text-[#B1897C] text-[10px] md:text-xs mt-2">
              {isExpiredTrial 
                ? "Your trial has ended. Upgrade to continue your journey with Dea."
                : "Dea remembers your conversation and learns about your goals"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
